import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buscarConfiguracoesConferencia, atualizarConfiguracoesConferencia } from '../../../services/configuracoesConferencia';
import type { ConfiguracaoConferencia } from '../../../services/configuracoesConferencia';

const ConfiguracoesConferencia: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [, setConfig] = useState<ConfiguracaoConferencia | null>(null);
  const [formData, setFormData] = useState({
    exibir_doca: true,
    modo_carregamento: 'palete_individual' as 'palete_individual' | 'lista_paletes',
    modo_pre_carregamento: 'palete_individual' as 'palete_individual' | 'lista_paletes',
    modo_descarregamento: 'bipar_palete' as 'bipar_palete' | 'auto_detectar',
    exibir_area_descarregamento: true,
    habilitar_camera_scanner: false,
    acao_paletes_pendentes: 'transferir' as 'nenhuma' | 'transferir' | 'excluir' | 'ocorrencia'
  });
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    carregarConfiguracoes();
  }, []);

  const carregarConfiguracoes = async () => {
    try {
      const result = await buscarConfiguracoesConferencia();
      if (result.success && result.configuracao) {
        setConfig(result.configuracao);
        setFormData({
          exibir_doca: result.configuracao.exibir_doca !== false,
          modo_carregamento: result.configuracao.modo_carregamento || 'palete_individual',
          modo_pre_carregamento: result.configuracao.modo_pre_carregamento || 'palete_individual',
          modo_descarregamento: result.configuracao.modo_descarregamento || 'bipar_palete',
          exibir_area_descarregamento: result.configuracao.exibir_area_descarregamento !== false,
          habilitar_camera_scanner: result.configuracao.habilitar_camera_scanner === true,
          acao_paletes_pendentes: result.configuracao.acao_paletes_pendentes || 'transferir'
        });
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvar = async () => {
    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const result = await atualizarConfiguracoesConferencia(formData);
      if (result.success) {
        setConfig(result.configuracao);
        setSuccessMsg('Configurações salvas com sucesso!');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <div style={{ textAlign: 'center' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#3498db' }}></i>
            <p style={{ marginTop: 12, color: '#7f8c8d' }}>Carregando configurações...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <button
        className="conferencia-back-link"
        onClick={() => navigate('/conferencia')}
        style={{ background: 'none', border: 'none', color: '#3498db', cursor: 'pointer', fontSize: '0.9rem', marginBottom: 16, padding: 0 }}
      >
        <i className="fas fa-arrow-left"></i> Voltar
      </button>

      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="fas fa-cog" style={{ color: '#04C64F' }}></i>
            Configurações de Conferência
          </h2>
          <p style={{ color: '#7f8c8d', fontSize: '0.9rem', marginTop: 4 }}>
            Configure o comportamento geral das conferências de pré-carregamento, carregamento e descarregamento.
          </p>
        </div>

        {successMsg && (
          <div style={{
            padding: '12px 16px', marginBottom: 16, borderRadius: 8,
            background: '#e8f5e9', border: '1px solid #c8e6c9', color: '#2e7d32',
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <i className="fas fa-check-circle"></i>
            <span>{successMsg}</span>
          </div>
        )}

        {errorMsg && (
          <div style={{
            padding: '12px 16px', marginBottom: 16, borderRadius: 8,
            background: '#fce4ec', border: '1px solid #ffcdd2', color: '#c62828',
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <i className="fas fa-exclamation-circle"></i>
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Configurações de Pré-Carregamento */}
        <div style={{
          background: 'white', borderRadius: 12, padding: 24, marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0'
        }}>
          <h3 style={{
            marginBottom: 20, color: '#2c3e50',
            borderBottom: '2px solid #8e44ad', paddingBottom: 10,
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <i className="fas fa-pallet" style={{ color: '#8e44ad' }}></i>
            Pré-Carregamento
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            <div className="form-group">
              <label className="form-label">Modo de Pré-Carregamento</label>
              <select
                name="modo_pre_carregamento"
                value={formData.modo_pre_carregamento}
                onChange={(e) => setFormData(prev => ({ ...prev, modo_pre_carregamento: e.target.value as 'palete_individual' | 'lista_paletes' }))}
                className="form-control"
              >
                <option value="palete_individual">Palete Individual (digitar/bipar número)</option>
                <option value="lista_paletes">Lista de Paletes (selecionar da lista)</option>
              </select>
              <span style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 4, display: 'block' }}>
                Define se o conferente digita o número do palete ou seleciona de uma lista com os paletes da montagem de carga do dia
              </span>
            </div>
          </div>
        </div>

        {/* Configurações de Carregamento */}
        <div style={{
          background: 'white', borderRadius: 12, padding: 24, marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0'
        }}>
          <h3 style={{
            marginBottom: 20, color: '#2c3e50',
            borderBottom: '2px solid #04C64F', paddingBottom: 10,
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <i className="fas fa-truck-loading" style={{ color: '#04C64F' }}></i>
            Carregamento
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            <div className="form-group">
              <label className="form-label">Solicitar Doca no Carregamento</label>
              <select
                name="exibir_doca"
                value={formData.exibir_doca ? 'true' : 'false'}
                onChange={(e) => setFormData(prev => ({ ...prev, exibir_doca: e.target.value === 'true' }))}
                className="form-control"
              >
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
              <span style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 4, display: 'block' }}>
                Quando desativado, o campo de doca não será solicitado na conferência de carregamento
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">Modo de Carregamento</label>
              <select
                name="modo_carregamento"
                value={formData.modo_carregamento}
                onChange={(e) => setFormData(prev => ({ ...prev, modo_carregamento: e.target.value as 'palete_individual' | 'lista_paletes' }))}
                className="form-control"
              >
                <option value="palete_individual">Palete Individual (bipar um a um)</option>
                <option value="lista_paletes">Lista de Paletes (selecionar da lista)</option>
              </select>
              <span style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 4, display: 'block' }}>
                Define se o conferente bipa palete por palete ou seleciona de uma lista
              </span>
            </div>
          </div>
        </div>

        {/* Configurações de Descarregamento */}
        <div style={{
          background: 'white', borderRadius: 12, padding: 24, marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0'
        }}>
          <h3 style={{
            marginBottom: 20, color: '#2c3e50',
            borderBottom: '2px solid #3498db', paddingBottom: 10,
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <i className="fas fa-truck-ramp-box" style={{ color: '#3498db' }}></i>
            Descarregamento
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            <div className="form-group">
              <label className="form-label">Modo de Descarregamento</label>
              <select
                name="modo_descarregamento"
                value={formData.modo_descarregamento}
                onChange={(e) => setFormData(prev => ({ ...prev, modo_descarregamento: e.target.value as 'bipar_palete' | 'auto_detectar' }))}
                className="form-control"
              >
                <option value="bipar_palete">Bipar Palete (informar palete manualmente)</option>
                <option value="auto_detectar">Auto Detectar (identificar palete pela etiqueta)</option>
              </select>
              <span style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 4, display: 'block' }}>
                Define se o conferente precisa bipar o palete primeiro ou se ele será identificado automaticamente pela etiqueta
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">Solicitar Área de Entrega no Descarregamento</label>
              <select
                name="exibir_area_descarregamento"
                value={formData.exibir_area_descarregamento ? 'true' : 'false'}
                onChange={(e) => setFormData(prev => ({ ...prev, exibir_area_descarregamento: e.target.value === 'true' }))}
                className="form-control"
              >
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
              <span style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 4, display: 'block' }}>
                Quando desativado, a digitação da área de entrega não será solicitada no descarregamento
              </span>
            </div>
          </div>
        </div>

        {/* Configurações de Montagem de Carga */}
        <div style={{
          background: 'white', borderRadius: 12, padding: 24, marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0'
        }}>
          <h3 style={{
            marginBottom: 20, color: '#2c3e50',
            borderBottom: '2px solid #e74c3c', paddingBottom: 10,
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <i className="fas fa-boxes-stacked" style={{ color: '#e74c3c' }}></i>
            Montagem de Carga
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            <div className="form-group">
              <label className="form-label">Paletes Pendentes ao Abrir Nova Montagem</label>
              <select
                name="acao_paletes_pendentes"
                value={formData.acao_paletes_pendentes}
                onChange={(e) => setFormData(prev => ({ ...prev, acao_paletes_pendentes: e.target.value as 'nenhuma' | 'transferir' | 'excluir' | 'ocorrencia' }))}
                className="form-control"
              >
                <option value="nenhuma">Não fazer nada</option>
                <option value="transferir">Transferir para a nova montagem</option>
                <option value="excluir">Excluir paletes pendentes</option>
                <option value="ocorrencia">Registrar ocorrência e inutilizar palete</option>
              </select>
              <span style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 4, display: 'block' }}>
                Define o que acontece com paletes que não receberam nenhuma etiqueta na montagem anterior quando uma nova montagem de carga é aberta para o mesmo cliente.
                <strong> Não fazer nada:</strong> Mantém os paletes pendentes na montagem anterior sem alterações.
                <strong> Transferir:</strong> Move os paletes pendentes para a nova montagem automaticamente.
                <strong> Excluir:</strong> Remove os paletes pendentes da montagem anterior.
                <strong> Ocorrência:</strong> Registra uma ocorrência automática de "Palete Não Utilizado" e inutiliza o palete, impedindo qualquer leitura ou pesquisa.
              </span>
            </div>
          </div>
        </div>

        {/* Configurações Gerais */}
        <div style={{
          background: 'white', borderRadius: 12, padding: 24, marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0'
        }}>
          <h3 style={{
            marginBottom: 20, color: '#2c3e50',
            borderBottom: '2px solid #e67e22', paddingBottom: 10,
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <i className="fas fa-sliders-h" style={{ color: '#e67e22' }}></i>
            Configurações Gerais
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            <div className="form-group">
              <label className="form-label">Leitura por Câmera do Celular</label>
              <select
                name="habilitar_camera_scanner"
                value={formData.habilitar_camera_scanner ? 'true' : 'false'}
                onChange={(e) => setFormData(prev => ({ ...prev, habilitar_camera_scanner: e.target.value === 'true' }))}
                className="form-control"
              >
                <option value="false">Desabilitado</option>
                <option value="true">Habilitado</option>
              </select>
              <span style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 4, display: 'block' }}>
                Quando habilitado, exibe um ícone de câmera nos campos de leitura de etiquetas, permitindo escanear
                QR Code e códigos de barras usando a câmera do celular. Ideal para conferentes que não utilizam coletor de dados.
              </span>
            </div>
          </div>
        </div>

        {/* Botão Salvar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button
            type="button"
            onClick={handleSalvar}
            disabled={saving}
            style={{
              padding: '12px 32px', borderRadius: 8, border: 'none',
              background: '#04C64F', color: 'white', fontWeight: 600,
              fontSize: '0.95rem', cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? (
              <><i className="fas fa-spinner fa-spin"></i> Salvando...</>
            ) : (
              <><i className="fas fa-save"></i> Salvar Configurações</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracoesConferencia;
